import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * COMPONENTE: PlaylistMetricsCards
 * 
 * Tarjetas de métricas y KPIs en tiempo real para la consola de control general de Arma la Playlist.
 * Muestra 6 indicadores clave en un grid responsivo con diseño de alto impacto visual.
 */
export const PlaylistMetricsCards = ({
    metrics = {},
    suggestions = [],
    activePlaylist = []
}) => {
    // 1. Total Sugerencias
    const totalSuggestions = useMemo(() => {
        if (metrics && typeof metrics.totalSuggestions === 'number') {
            return metrics.totalSuggestions;
        }
        return (suggestions || []).length;
    }, [metrics, suggestions]);

    // 2. En Playlist Activa
    const activePlaylistCount = useMemo(() => {
        if (metrics && typeof metrics.activePlaylistCount === 'number') {
            return metrics.activePlaylistCount;
        }
        if (metrics && typeof metrics.favoritesCount === 'number') {
            return metrics.favoritesCount;
        }
        return (activePlaylist || []).length;
    }, [metrics, activePlaylist]);

    // 3. Reproducidas
    const playedCount = useMemo(() => {
        if (metrics && typeof metrics.playedCount === 'number') {
            return metrics.playedCount;
        }
        return (activePlaylist || []).filter(x => x.isPlayed).length;
    }, [metrics, activePlaylist]);

    // 4. Altas por Operador
    const manualCount = useMemo(() => {
        if (metrics && typeof metrics.manualCount === 'number') {
            return metrics.manualCount;
        }
        return (suggestions || []).filter(x => x.isManualEntry).length;
    }, [metrics, suggestions]);

    // 5. Aficionados Participantes
    const uniqueFansCount = useMemo(() => {
        if (metrics && typeof metrics.uniqueFansCount === 'number') {
            return metrics.uniqueFansCount;
        }
        if (metrics && typeof metrics.uniqueFans === 'number') {
            return metrics.uniqueFans;
        }
        const fans = new Set(
            (suggestions || [])
                .filter(x => !x.isManualEntry && (x.fanName || x.fanSessionId))
                .map(x => (x.fanName || x.fanSessionId).trim())
        );
        return fans.size;
    }, [metrics, suggestions]);

    // 6. Canción Más Votada
    const topVotedInfo = useMemo(() => {
        if (metrics && metrics.topVotedSongTitle) {
            return {
                title: metrics.topVotedSongTitle,
                artist: metrics.topVotedSongArtist || '',
                votes: metrics.topVotedSongVotes || 0
            };
        }
        if (metrics && Array.isArray(metrics.topVotedSongs) && metrics.topVotedSongs.length > 0) {
            const top = metrics.topVotedSongs[0];
            return {
                title: top.title || 'N/A',
                artist: top.artistName || '',
                votes: top.voteCount || 0
            };
        }
        if (suggestions && suggestions.length > 0) {
            const top = suggestions.reduce((max, cur) => {
                const curVotes = cur.voteCount || 0;
                const maxVotes = max ? (max.voteCount || 0) : -1;
                return curVotes > maxVotes ? cur : max;
            }, null);

            if (top && (top.voteCount || 0) > 0) {
                return {
                    title: top.title,
                    artist: top.artistName || '',
                    votes: top.voteCount || 0
                };
            }
        }
        return {
            title: 'Sin votos aún',
            artist: '',
            votes: 0
        };
    }, [metrics, suggestions]);

    return (
        <section className="PlaylistMetricsCards" aria-label="Métricas de la dinámica">
            <div className="PlaylistMetricsCards__Grid">
                {/* 1. Total Sugerencias */}
                <div className="PlaylistMetricsCard PlaylistMetricsCard--Suggestions">
                    <div className="PlaylistMetricsCard__IconWrapper">
                        <i className="bi bi-inbox-fill"></i>
                    </div>
                    <div className="PlaylistMetricsCard__Content">
                        <span className="PlaylistMetricsCard__Label">Total Sugerencias</span>
                        <span className="PlaylistMetricsCard__Value">{totalSuggestions}</span>
                    </div>
                    <div className="PlaylistMetricsCard__Glow"></div>
                </div>

                {/* 2. En Playlist Activa */}
                <div className="PlaylistMetricsCard PlaylistMetricsCard--Active">
                    <div className="PlaylistMetricsCard__IconWrapper">
                        <i className="bi bi-star-fill"></i>
                    </div>
                    <div className="PlaylistMetricsCard__Content">
                        <span className="PlaylistMetricsCard__Label">En Playlist Activa</span>
                        <span className="PlaylistMetricsCard__Value">{activePlaylistCount}</span>
                    </div>
                    <div className="PlaylistMetricsCard__Glow"></div>
                </div>

                {/* 3. Reproducidas */}
                <div className="PlaylistMetricsCard PlaylistMetricsCard--Played">
                    <div className="PlaylistMetricsCard__IconWrapper">
                        <i className="bi bi-check2-circle"></i>
                    </div>
                    <div className="PlaylistMetricsCard__Content">
                        <span className="PlaylistMetricsCard__Label">Reproducidas</span>
                        <span className="PlaylistMetricsCard__Value">{playedCount}</span>
                    </div>
                    <div className="PlaylistMetricsCard__Glow"></div>
                </div>

                {/* 4. Altas por Operador */}
                <div className="PlaylistMetricsCard PlaylistMetricsCard--Manual">
                    <div className="PlaylistMetricsCard__IconWrapper">
                        <i className="bi bi-headphones"></i>
                    </div>
                    <div className="PlaylistMetricsCard__Content">
                        <span className="PlaylistMetricsCard__Label">Altas por Operador</span>
                        <span className="PlaylistMetricsCard__Value">{manualCount}</span>
                    </div>
                    <div className="PlaylistMetricsCard__Glow"></div>
                </div>

                {/* 5. Aficionados Participantes */}
                <div className="PlaylistMetricsCard PlaylistMetricsCard--Fans">
                    <div className="PlaylistMetricsCard__IconWrapper">
                        <i className="bi bi-people-fill"></i>
                    </div>
                    <div className="PlaylistMetricsCard__Content">
                        <span className="PlaylistMetricsCard__Label">Aficionados Participantes</span>
                        <span className="PlaylistMetricsCard__Value">{uniqueFansCount}</span>
                    </div>
                    <div className="PlaylistMetricsCard__Glow"></div>
                </div>

                {/* 6. Canción Más Votada */}
                <div className="PlaylistMetricsCard PlaylistMetricsCard--TopVoted" title={`${topVotedInfo.title}${topVotedInfo.artist ? ' - ' + topVotedInfo.artist : ''}`}>
                    <div className="PlaylistMetricsCard__IconWrapper">
                        <i className="bi bi-fire"></i>
                    </div>
                    <div className="PlaylistMetricsCard__Content">
                        <div className="d-flex align-items-center justify-content-between gap-1">
                            <span className="PlaylistMetricsCard__Label">Canción Más Votada</span>
                            {topVotedInfo.votes > 0 && (
                                <span className="PlaylistMetricsCard__VoteBadge">
                                    <i className="bi bi-heart-fill me-1"></i>{topVotedInfo.votes}
                                </span>
                            )}
                        </div>
                        <span className="PlaylistMetricsCard__Value PlaylistMetricsCard__Value--Song text-truncate">
                            {topVotedInfo.title}
                        </span>
                        {topVotedInfo.artist && (
                            <span className="PlaylistMetricsCard__SubValue text-truncate">
                                {topVotedInfo.artist}
                            </span>
                        )}
                    </div>
                    <div className="PlaylistMetricsCard__Glow"></div>
                </div>
            </div>
        </section>
    );
};

PlaylistMetricsCards.propTypes = {
    metrics: PropTypes.object,
    suggestions: PropTypes.array,
    activePlaylist: PropTypes.array
};

export default PlaylistMetricsCards;
